import type { OrganizationAssignmentType } from '@today/types'
import type { OrgAssignmentDTO } from '@/services/organization.service'
import { collect, dateToIso, type FieldErrors, isUuid, validityError } from './form-rules'

/** The Assign dialog as edited (TO-389 organization-admin-ui REQ-008). */
export interface AssignmentFormValues {
	userId: string
	assignmentType: OrganizationAssignmentType | ''
	isPrimary: boolean
	validFrom: string
	validTo: string
}

export const EMPTY_ASSIGNMENT_FORM: AssignmentFormValues = {
	userId: '',
	assignmentType: '',
	isPrimary: false,
	validFrom: '',
	validTo: '',
}

const userIdError = (userId: string, userDirectory: boolean): string | null => {
	if (userId.trim() === '') return 'validation.required'
	// Without `user.read` the id is typed by hand: only a UUID is accepted.
	return userDirectory || isUuid(userId) ? null : 'validation.uuid'
}

export const assignmentFormErrors = (
	v: AssignmentFormValues,
	{ userDirectory }: { userDirectory: boolean },
): FieldErrors =>
	collect({
		userId: userIdError(v.userId, userDirectory),
		assignmentType: v.assignmentType === '' ? 'validation.required' : null,
		validTo: validityError(v.validFrom, v.validTo),
	})

export const buildAssignmentBody = (v: AssignmentFormValues, unitCode: string): Record<string, unknown> => ({
	userId: v.userId.trim(),
	unitCode,
	assignmentType: v.assignmentType,
	isPrimary: v.isPrimary,
	...(v.validFrom ? { validFrom: dateToIso(v.validFrom) } : {}),
	...(v.validTo ? { validTo: dateToIso(v.validTo) } : {}),
})

/** The user's display name, or the raw id when the directory does not know it. */
export const displayNameOf = (names: ReadonlyMap<string, string>, userId: string): string => names.get(userId) ?? userId

/** CURRENT while open-ended or not yet lapsed (TO-412). */
export const isCurrent = (a: OrgAssignmentDTO, now: Date): boolean =>
	a.validTo === null || new Date(a.validTo).getTime() > now.getTime()

export const canMakePrimary = (a: OrgAssignmentDTO, now: Date): boolean => !a.isPrimary && isCurrent(a, now)
